import React from 'react';
import { usePlaybook } from '../../context/PlaybookContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import { formatCurrency } from '../../utils/helpers';

const CoachFeed = ({ projectId }) => {
  const { coachEvents, runAiNextBestAction, battlecards } = usePlaybook();
  const { deals } = useCRM();
  
  const projectEvents = coachEvents.filter(e => e.project_id === projectId);
  
  // Calculate simple stats
  const projectDeals = deals.filter(d => d.project_id === projectId);
  const pipelineValue = projectDeals.reduce((sum, d) => sum + Number(d.value), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
      {/* Feed Column */}
      <div className="lg:col-span-2 space-y-6">
        <div className="flex justify-between items-center bg-indigo-50 p-6 rounded-2xl border border-indigo-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm">
              <SafeIcon icon="🤖" className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-black text-indigo-900 text-lg">AI Coach</h3>
              <p className="text-indigo-700 text-sm">Jeg analyserer prosjektet kontinuerlig.</p>
            </div>
          </div>
          <button 
            onClick={() => runAiNextBestAction(projectId)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all flex items-center gap-2"
          >
            <SafeIcon icon="✨" /> Analyser nå
          </button>
        </div>

        <div className="space-y-4">
          {projectEvents.length > 0 ? projectEvents.map(event => (
            <div key={event.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-indigo-500 to-purple-500" />
              <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50 px-2 py-1 rounded-lg">
                  {event.event_type}
                </span>
                <span className="text-xs text-slate-400">{new Date(event.created_at).toLocaleTimeString()}</span>
              </div>
              <p className="text-slate-800 font-medium leading-relaxed">
                {event.message}
              </p>
              <div className="mt-4 pt-4 border-t border-slate-50 flex gap-2">
                <button className="text-xs font-bold text-slate-500 hover:text-indigo-600 flex items-center gap-1">
                  <SafeIcon icon="👍" /> Nyttig
                </button>
                <button className="text-xs font-bold text-slate-500 hover:text-rose-600 flex items-center gap-1 ml-4">
                  <SafeIcon icon="👎" /> Ikke relevant
                </button>
              </div>
            </div>
          )) : (
            <div className="text-center py-12">
              <p className="text-slate-400 text-sm">Ingen forslag enda. Trykk "Analyser nå".</p>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar: Battlecards & Context */}
      <div className="space-y-6">
        <div className="bg-slate-900 text-white p-6 rounded-[2rem] shadow-xl">
          <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Prosjekt Context</h4>
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Pipeline Verdi</p>
              <p className="text-2xl font-black text-white">{formatCurrency(pipelineValue)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Aktive Deals</p>
              <p className="text-xl font-bold text-white">{projectDeals.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
          <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
            <SafeIcon icon="⚔️" /> Battlecards (Match)
          </h4>
          <div className="space-y-3">
            {battlecards.filter(b => b.project_id === projectId).map(card => (
              <div key={card.id} className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:bg-slate-100 transition-colors cursor-pointer group">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-bold text-slate-800 text-sm">{card.competitor_name}</span>
                  <SafeIcon icon="➡️" className="w-3 h-3 text-slate-300 group-hover:text-primary-600" />
                </div>
                <p className="text-xs text-slate-500 line-clamp-2">{card.response}</p>
              </div>
            ))}
            {battlecards.length === 0 && <p className="text-xs text-slate-400 italic">Ingen battlecards funnet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachFeed;